import { EasyVrpMapManager } from './EasyVrpMap/EasyVrpMapManager';
import {
    AppOptionsSchema,
    type AppOptionsType,
    CONSTRAINTS,
    type NucleiType,
    AppOptionsDefault,
} from './EasyVrpApp.types';
import { EasyVrpResultApp } from './EasyVrpResultApp';
import type { Api } from '@automapa/svelte-automapa-template';
import {
    type CalculationIdInProgressType,
    type CountPointsResponseType,
    EasyVrpApi,
} from './EasyVrpApi';
import { EasyVrpMapContext } from './EasyVrpMap/EasyVrpMapContext';
import type { Vehicle } from '../Vehicles/Vehicle';

/*
https://er.automapa.pl/issues/45951
godzina wyjazdu z magazynu (hh:m0) 00:00…23:30 (domyślnie taka jak w ustawieniach, np. 08:00; rozdzielczość 30 min)
 */

export class EasyVrpApp {
    private mapManager: EasyVrpMapManager = new EasyVrpMapManager({
        onNucleiDragEnd: (x: number, y: number, vehicle_id: number) => {
            console.log('#map onNucleiDragEnd:', x, y, vehicle_id);
            this.updateNuclei();
            this._onChange(this.getOptions());
        },
    });
    private timeoutHandler: ReturnType<typeof setTimeout> | null = null;
    private resultApp: EasyVrpResultApp = new EasyVrpResultApp();
    private currentVrpCalcId: number | null = null;
    private appOptions: AppOptionsType = AppOptionsDefault;
    private countStats: CountPointsResponseType = {
        historical_jobs: 0,
        unassigned_jobs: 0,
    };

    public toggleSettingsDisplayOrderOnMap(): void {
        this.mapManager._settings_display_order_on_map =
            !this.mapManager._settings_display_order_on_map;
    }

    public getSettingsDisplayOrderOnMap(): boolean {
        return this.mapManager._settings_display_order_on_map;
    }

    public toggleSettingsDisplayNucleiOnMap(): void {
        this.mapManager._settings_display_nuclei_on_map =
            !this.mapManager._settings_display_nuclei_on_map;
    }

    public getSettingsDisplayNucleiOnMap(): boolean {
        return this.mapManager._settings_display_nuclei_on_map;
    }

    public getResultsApp(): EasyVrpResultApp {
        return this.resultApp;
    }

    public setDefaultServiceTimeOnStopIfNotSet(value: number, force: boolean = false): void {
        if (this.appOptions.service_time_on_stop === null || force) {
            this.appOptions.service_time_on_stop = value * 60; // Convert minutes to seconds
        }
    }

    public setDefaultServiceTimePerPackageIfNotSet(value: number, force: boolean = false): void {
        if (this.appOptions.service_time_per_package === null || force) {
            this.appOptions.service_time_per_package = value * 60; // Convert minutes to seconds
        }
    }

    public setDefaultCostPerKmIfNotSet(value: number, force: boolean = false): void {
        if (this.appOptions.cost_per_km === null || force) {
            this.appOptions.cost_per_km = value;
        }
    }

    public setDefaultCostPerMinuteIfNotSet(value: number, force: boolean = false): void {
        if (this.appOptions.cost_per_minute === null || force) {
            this.appOptions.cost_per_minute = value;
        }
    }

    public setDefaultCostPerItemIfNotSet(value: number, force: boolean = false): void {
        if (this.appOptions.cost_per_item === null || force) {
            this.appOptions.cost_per_item = value;
        }
    }

    public setDefaultWarehouseStartTimeIfNotSet(value: string, force: boolean = false): void {
        if (this.appOptions.start_time_from_warehouse === null || force) {
            this.appOptions.start_time_from_warehouse = value;
        }
    }

    public setSelectedDay(day: string | null): void {
        this.appOptions.selected_day = day;
        this._onChange(this.getOptions());
    }

    // public addNucleiToMapFromQuickCalc(
    //     vehicles: Vehicle[] = [],
    //     zoomToBounds: boolean = false
    // ): void {
    //     this.mapManager.nucleiManager.clearMap();
    //     this.resultNuclei.forEach(nuclei => {
    //         const [x, y] = nuclei.geom.coordinates;
    //         const color = nuclei.color;
    //         const opacity = 0.8;
    //         const position = {
    //             vehicle: vehicles.find(v => v.getId() === nuclei.id) || new Vehicle(),
    //         };
    //         this.mapManager.nucleiManager.addNuclei(x, y, color, opacity, position);
    //     });
    //     if (zoomToBounds) {
    //         this.mapManager.nucleiManager.zoomToBounds();
    //     }
    //     this.mapManager.nucleiManager.addNucleiToMap(this.mapManager.getMapContext());
    // }

    public setVrpCalcId(requestId: number): void {
        this.currentVrpCalcId = requestId;
    }

    public getVrpCalcId(): number | null {
        return this.currentVrpCalcId;
    }

    public clearVrpCalcId() {
        this.currentVrpCalcId = null;
    }

    public getMapManager(): EasyVrpMapManager {
        return this.mapManager;
    }

    private readonly _onChange: (options: AppOptionsType) => void;

    constructor(onChange: (options: AppOptionsType) => void) {
        this._onChange = onChange;
    }

    public triggerChange(): void {
        this._onChange(this.getOptions());
    }

    setSelectedVehicleIds(ids: number[]): void {
        this.appOptions.vehicle_ids = ids;
        this._onChange(this.getOptions());
    }

    setOptions(options: Partial<AppOptionsType>): void {
        const parsedOptions = AppOptionsSchema.partial().parse(options);
        this.appOptions = { ...this.appOptions, ...parsedOptions };
    }

    getOptions(): AppOptionsType {
        this.updateNuclei();
        return this.appOptions;
    }

    setSolverType(solverType: string): void {
        this.appOptions.solver_type = solverType;
        this._onChange(this.getOptions());
    }

    updateNuclei(): void {
        this.appOptions.nuclei = [];
        this.mapManager.getMapNuclei().forEach(point => {
            const vehicleId = point.getVehicleId();
            if (!vehicleId) {
                console.warn('Point does not have a vehicle ID:', point);
                return;
            }
            this.appOptions.nuclei.push({
                vehicle_id: vehicleId,
                coordinates: { x: point.getX(), y: point.getY() },
            });
            // if (vehicleId) {
            //     const existingNuclei: NucleiType | undefined = this.appOptions.nuclei.find(
            //         n => n.vehicle_id === vehicleId
            //     );
            //     if (existingNuclei) {
            //         existingNuclei.coordinates = { x: point.getX(), y: point.getY() };
            //     } else {
            //         this.appOptions.nuclei.push({
            //             vehicle_id: vehicleId,
            //             coordinates: { x: point.getX(), y: point.getY() },
            //         });
            //     }
            // } else {
            //     console.warn('Point does not have a vehicle ID:', point);
            // }
        });
    }

    getSelectedGroupedVehicleIds(): number[] {
        return this.appOptions.group_and_vehicle_ids || [];
    }

    setSelectedGroupedVehicleIds(ids: number[]): void {
        this.appOptions.group_and_vehicle_ids = ids;
        this._onChange(this.getOptions());
    }

    getSelectedVehicleIds(): number[] {
        return this.appOptions.vehicle_ids || [];
    }

    setMaxVehicles(value: number): void {
        this.appOptions.max_vehicles = value;
        this._onChange(this.getOptions());
    }

    setMinVehicles(value: number): void {
        this.appOptions.min_vehicles = value;
        this._onChange(this.getOptions());
    }

    getMaxVehicles(): number | null {
        return this.appOptions.max_vehicles;
    }

    getMinVehicles(): number | null {
        return this.appOptions.min_vehicles;
    }

    getOptionsDetails(field: string): { min: number; max: number; default: number } {
        // @ts-ignore
        const constraints = CONSTRAINTS[field];
        if (constraints) {
            return {
                min: constraints.min,
                max: constraints.max,
                default: constraints?.default ?? null,
            };
        }
        throw new Error(`Unknown field: ${field}`);
    }

    destroy(): void {
        this.mapManager.clearMapElements();
        this.clearTimeoutToCheckResults();
    }

    calculationInProgress(): boolean {
        return this.resultApp
            .getResults()
            .some(result => result.calculated === null && result.error === null);
    }

    async getCalculationIdsInProgress(api: Api): Promise<CalculationIdInProgressType[]> {
        let calculationsInProgress: CalculationIdInProgressType[] =
            await EasyVrpApi.getCalculationIdsInProgress(api);
        calculationsInProgress.forEach(calc => {
            this.resultApp.updateProgressFloat(calc.id, calc.progress_float);
        });
        return calculationsInProgress || [];
    }

    async getCalculationIdsThatNeedToBeLoadedFromApi(
        api: Api,
        inProgressIds: CalculationIdInProgressType[]
    ): Promise<number[]> {
        // find calculation id from results that is not in getCalculationIdsInProgress and in getCalculationIdsInProgressFromResults
        return this.resultApp
            .getResults()
            .filter(result => result.calculated === null && result.error === null)
            .map(result => result.id)
            .filter(id => !inProgressIds.map(r => r.id).includes(id));
    }

    setTimeoutToCheckResults(
        seconds: number,
        api: Api,
        onTimeout: () => void,
        vehicles: Vehicle[]
    ): void {
        console.log(
            '#setTimeoutToCheckResults, Setting timeout to check VRP calculation results in',
            seconds,
            'seconds'
        );
        this.timeoutHandler = setTimeout(() => {
            if (this.calculationInProgress()) {
                console.log(
                    '#setTimeoutToCheckResults, Checking VRP calculation results...',
                    this.getVrpCalcId()
                );
                this.getCalculationIdsInProgress(api)
                    .then(inProgressIds => {
                        console.log(
                            '#setTimeoutToCheckResults, In-progress calculation IDs:',
                            inProgressIds
                        );
                        // update progressFloat
                        inProgressIds.forEach(calc => {
                            this.resultApp.updateProgressFloat(calc.id, calc.progress_float);
                        });
                        onTimeout();
                        this.getCalculationIdsThatNeedToBeLoadedFromApi(api, inProgressIds)
                            .then(ids => {
                                ids.forEach(id => {
                                    EasyVrpApi.getAutodispatchResults(api, id)
                                        .then(result => {
                                            if (result.status === 'pending') {
                                                console.info(
                                                    '#setTimeoutToCheckResults, VRP calculation is still pending, skipping result loading',
                                                    result
                                                );
                                                return;
                                            }
                                            console.log('Autodispatch results:', result);
                                            if (id === null) {
                                                console.warn(
                                                    '#setTimeoutToCheckResults, VRP calculation ID is null, cannot load results'
                                                );
                                                return;
                                            }
                                            this.getResultsApp()
                                                .loadResults(api)
                                                .then(() => {
                                                    console.log(
                                                        '#setTimeoutToCheckResults, Results loaded successfully'
                                                    );
                                                })
                                                .catch(error => {
                                                    console.error(
                                                        '#setTimeoutToCheckResults, Error loading results:',
                                                        error
                                                    );
                                                })
                                                .finally(() => {
                                                    onTimeout();
                                                });
                                            // this.clearVrpCalcId();
                                            // this.getResultsApp()
                                            //     .loadResultDetails(api, id)
                                            //     .then(result => {
                                            //         console.log(
                                            //             '#setTimeoutToCheckResults, Result details loaded successfully'
                                            //         );
                                            //         if (!result?.routesToDraw) {
                                            //             console.warn(
                                            //                 '#setTimeoutToCheckResults, No routes to draw in result details'
                                            //             );
                                            //             return;
                                            //         }
                                            //         if (!result?.extra_data) {
                                            //             console.warn(
                                            //                 '#setTimeoutToCheckResults, No extra data in result details'
                                            //             );
                                            //             return;
                                            //         }
                                            //         try {
                                            //             let mapContext = new EasyVrpMapContext(
                                            //                 result.id,
                                            //                 result.routesToDraw,
                                            //                 vehicles,
                                            //                 result.extra_data
                                            //             );
                                            //             this.getMapManager().drawMapElements(mapContext);
                                            //         } catch (error) {
                                            //             console.error(
                                            //                 '#setTimeoutToCheckResults, Error creating EasyVrpMapContext:',
                                            //                 error
                                            //             );
                                            //         }
                                            //     })
                                            //     .catch(error => {
                                            //         this.clearTimeoutToCheckResults();
                                            //         console.error(
                                            //             '#setTimeoutToCheckResults, 2 Error loading result details:',
                                            //             error
                                            //         );
                                            //     });
                                        })
                                        .catch(error => {
                                            this.clearTimeoutToCheckResults();
                                            console.error(
                                                '#setTimeoutToCheckResults, 3 Error fetching autodispatch results:',
                                                error
                                            );
                                        });
                                });
                            })
                            .catch(error => {
                                this.clearTimeoutToCheckResults();
                                console.error(
                                    '#setTimeoutToCheckResults, Error getting calculation IDs that need to be loaded from API:',
                                    error
                                );
                                this.getResultsApp()
                                    .loadResults(api)
                                    .then(() => {
                                        console.log(
                                            '#setTimeoutToCheckResults, Results loaded successfully'
                                        );
                                    })
                                    .catch(error => {
                                        console.error(
                                            '#setTimeoutToCheckResults, Error loading results:',
                                            error
                                        );
                                    })
                                    .finally(() => {
                                        onTimeout();
                                    });
                            });
                    })
                    .catch(error => {
                        console.warn('cannot get calculation IDs in progress:', error);
                    });
            }
            this.setTimeoutToCheckResults(seconds, api, onTimeout, vehicles);
        }, seconds * 1000);
    }

    clearTimeoutToCheckResults(): void {
        if (this.timeoutHandler) {
            clearTimeout(this.timeoutHandler);
            this.timeoutHandler = null;
            console.log('Cleared timeout for checking VRP calculation results');
        }
    }

    public async loadCountStats(api: Api): Promise<CountPointsResponseType> {
        let stats: CountPointsResponseType = await EasyVrpApi.getCountPoints(api, this.appOptions);
        if (!stats) {
            console.error('Failed to load count points from API');
            stats = { historical_jobs: 0, unassigned_jobs: 0 };
        }
        this.countStats = {
            historical_jobs: stats.historical_jobs,
            unassigned_jobs: stats.unassigned_jobs,
        };
        console.log('Count countStats:', this.countStats);
        return this.countStats;
    }

    public getCountStats(): CountPointsResponseType {
        return this.countStats;
    }

    public async removeHistoryJobs(api: Api): Promise<void> {
        return EasyVrpApi.removeHistoryJobs(api);
    }

    public async assignCalculation(api: Api, calcId: number, selectedDay: string): Promise<any> {
        this.resultApp.setCalculationAssigned(calcId);
        return EasyVrpApi.assignCalculation(api, calcId, selectedDay);
    }
}
